import { css } from 'aphrodite';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import type { ISupportMailbox } from '../../../queries/Support/useSupportMailboxQuery';
import { ChevronLeft } from '../../../web/components/svgs/icons/ChevronLeft';
import { MoreIcon } from '../../../web/components/svgs/icons/MoreIcon';
import { Button } from '../../../web/components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../web/components-v2/Dropdown';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { InboxGlyph, ReportsGlyph, SettingsGlyph } from '../../pages/AdminSupportInboxPage/inboxIcons';
import { supportSharedStyles as ss } from './styles/supportSharedStyles';

export type SupportMastheadTab = 'inbox' | 'reports' | 'settings';

/** Sentinel Select value for the all-mailboxes reports view, offered to every support agent. */
export const ALL_MAILBOXES_VALUE = 'all';

interface SupportMastheadAppBarProps {
	activeTab: SupportMastheadTab;
	mailboxes: ISupportMailbox[];
	/** May be ALL_MAILBOXES_VALUE when `showAllMailboxesOption` is set. */
	selectedMailboxId: string;
	onSelectInbox: () => void;
	onSelectReports: () => void;
	onSelectSettings: () => void;
	onSelectMailbox: (mailboxId: string) => void;
	/** Offer a leading 'All mailboxes' item (value ALL_MAILBOXES_VALUE) ahead of the real mailboxes. */
	showAllMailboxesOption?: boolean;
	/**
	 * Show the mailbox switcher in the masthead. The inbox screen relocates it into the mailbox
	 * panel's channel selector, so it hides it here to avoid a duplicate control.
	 */
	showMailboxSwitcher?: boolean;
	showSettings?: boolean;
	/**
	 * Phone-only navigation state. Ticket details remains a URL state, not a separate route. While a
	 * ticket is open the phone header collapses to the back action alone — the ticket's own context
	 * header carries its subject, status, and actions.
	 */
	isTicketOpen?: boolean;
	onBackToInbox?: () => void;
	children?: React.ReactNode;
}

/**
 * Shared support desk masthead chrome. Keeps Inbox / Reports / Settings visible across support routes
 * so nested settings pages do not replace the app-level tabs with breadcrumbs.
 */
export function SupportMastheadAppBar({
	activeTab,
	mailboxes,
	selectedMailboxId,
	onSelectInbox,
	onSelectReports,
	onSelectSettings,
	onSelectMailbox,
	showAllMailboxesOption = false,
	showMailboxSwitcher = true,
	showSettings = true,
	isTicketOpen = false,
	onBackToInbox,
	children,
}: SupportMastheadAppBarProps) {
	const selectedMailbox = mailboxes.find(m => m.id === selectedMailboxId);
	const isPhoneLayout = useMediaQuery({ maxWidth: 767 });
	const mailboxItems = mailboxes.map(m => ({ value: m.id, label: m.name }));
	const selectItems = showAllMailboxesOption
		? [{ value: ALL_MAILBOXES_VALUE, label: 'All mailboxes' }, ...mailboxItems]
		: mailboxItems;
	const tabStyles = (tab: SupportMastheadTab) =>
		activeTab === tab ? [ss.supportTab, ss.supportTabActive] : [ss.supportTab];

	return (
		<div className={css(ss.supportAppBar)}>
			<div className={css(ss.supportDesktopControls)}>
				<div className={css(ss.supportTabRow)}>
					<Button type='button' variant='ghost' styles={tabStyles('inbox')} onClick={onSelectInbox}>
						<InboxGlyph size={16} />
						Inbox
					</Button>
					<Button type='button' variant='ghost' styles={tabStyles('reports')} onClick={onSelectReports}>
						<ReportsGlyph size={16} />
						Reports
					</Button>
					{showSettings && (
						<Button type='button' variant='ghost' styles={tabStyles('settings')} onClick={onSelectSettings}>
							<SettingsGlyph size={16} />
							Settings
						</Button>
					)}
				</div>
				{showMailboxSwitcher && (
					<>
						<div className={css(ss.supportTabDivider)} />
						{selectItems.length > 1 ? (
							<Select items={selectItems} value={selectedMailboxId} onValueChange={onSelectMailbox}>
								<SelectTrigger>
									<SelectValue placeholder='Mailbox' />
								</SelectTrigger>
								<SelectContent>
									{selectItems.map(item => (
										<SelectItem key={item.value} value={item.value}>
											{item.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						) : (
							<div className={css(ss.supportMailboxSwitcher)}>
								<span className={css(ss.supportMailboxDot)} />
								{selectedMailbox?.name ?? 'Mailbox'}
							</div>
						)}
					</>
				)}
				{children}
			</div>
			{isPhoneLayout && (
				<div className={css(ss.supportPhoneHeader)}>
					{isTicketOpen && onBackToInbox ? (
						<Button type='button' variant='ghost' styles={[ss.supportPhoneBack]} onClick={onBackToInbox}>
							<ChevronLeft width={20} height={20} />
							Inbox
						</Button>
					) : (
						<>
							{showMailboxSwitcher && selectItems.length > 1 ? (
								<Select items={selectItems} value={selectedMailboxId} onValueChange={onSelectMailbox}>
									<SelectTrigger styles={ss.supportPhoneMailboxTrigger}>
										<SelectValue placeholder='Mailbox' />
									</SelectTrigger>
									<SelectContent>
										{selectItems.map(item => (
											<SelectItem key={item.value} value={item.value}>
												{item.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							) : (
								<span className={css(ss.supportPhoneTitle)}>{selectedMailbox?.name || 'Inbox'}</span>
							)}
							<Dropdown>
								<DropdownTrigger
									render={
										<Button type='button' variant='ghost' styles={[ss.supportPhoneAction]} aria-label='Support menu' />
									}
								>
									<MoreIcon fillColor='currentColor' />
								</DropdownTrigger>
								<DropdownContent align='end' styles={[ss.supportPhoneMenu]}>
									<DropdownItem onClick={onSelectInbox}>Inbox</DropdownItem>
									<DropdownItem onClick={onSelectReports}>Reports</DropdownItem>
									{showSettings && <DropdownItem onClick={onSelectSettings}>Settings</DropdownItem>}
									<DropdownItem onClick={() => window.location.assign('/admin.html#/accounts')}>
										Admin home
									</DropdownItem>
								</DropdownContent>
							</Dropdown>
						</>
					)}
				</div>
			)}
		</div>
	);
}
